import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';
import { enrichRowsWithProfileAvatars, profileInitials, resolveProfileAvatarUrl } from '../../lib/profileAvatar.js';
import { Colors as C } from '../../constants/Colors.js';
import { Avatar } from './MarketplaceAtoms.js';
import { levelFromGroovesTotal, profileGroovesBalance, profileGroovesEarnedTotal } from '../constants/groovesEconomy.js';

export { levelFromGroovesTotal } from '../constants/groovesEconomy.js';

const BG = '#111827';
const BORDER = '#1e2d3d';
const SKELETON = '#1e2d3d';

const BASE_PROFILE_SELECT =
  'id, username, avatar_url, grooves_total, grooves_semana, grooves_mes, grooves_anio, grooves, level, grooves_earned_total';

function pointsForPeriod(row, period) {
  if (period === 'semana') return row.grooves_semana ?? profileGroovesBalance(row);
  if (period === 'mes') return row.grooves_mes ?? profileGroovesBalance(row);
  return row.grooves_anio ?? profileGroovesBalance(row);
}

function rankMedal(i) {
  if (i === 0) return '🥇';
  if (i === 1) return '🥈';
  if (i === 2) return '🥉';
  return `${i + 1}°`;
}

function mapRpcRow(r, period) {
  const pointsKey = period === 'semana' ? 'grooves_semana' : period === 'mes' ? 'grooves_mes' : 'grooves_anio';
  return {
    id: r.profile_id,
    username: r.username,
    avatar_url: resolveProfileAvatarUrl(r.avatar_url),
    [pointsKey]: r[pointsKey],
    grooves_semana: r.grooves_semana ?? 0,
    grooves_mes: r.grooves_mes ?? 0,
    grooves_anio: r.grooves_anio ?? 0,
    level: r.level ?? 1,
    grooves_earned_total: r.grooves_earned_total ?? 0,
  };
}

function mapProfileRow(p) {
  return {
    id: p.id,
    username: p.username,
    avatar_url: resolveProfileAvatarUrl(p.avatar_url),
    grooves_semana: p.grooves_semana ?? 0,
    grooves_mes: p.grooves_mes ?? 0,
    grooves_anio: p.grooves_anio ?? 0,
    grooves: p.grooves ?? 0,
    grooves_total: p.grooves_total,
    level: p.level ?? 1,
    grooves_earned_total: p.grooves_earned_total ?? 0,
  };
}

/**
 * Ranking por Grooves (Supabase RPC + perfiles verificados).
 *
 * @param {(page: string, opts?: object) => void} onNavigate
 * @param {() => void} [onViewFullRanking]
 * @param {number} [limit=10]
 * @param {boolean} [showViewFullLink=true]
 * @param {number|null} [listMaxHeight=420] — scroll en sidebar; null = sin límite (página completa)
 */
export default function TopGroovesRanking({
  onNavigate,
  onViewFullRanking,
  limit = 10,
  showViewFullLink = true,
  listMaxHeight = 420,
}) {
  const [period, setPeriod] = useState('semana');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [paddedFromProfiles, setPaddedFromProfiles] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPaddedFromProfiles(false);
    const safeLimit = Math.min(Math.max(Number(limit) || 10, 1), 500);

    const rpcName =
      period === 'semana'
        ? 'list_grooves_ranking_semana_activo'
        : period === 'mes'
          ? 'list_grooves_ranking_mes_activo'
          : 'list_grooves_ranking_anio_activo';

    const orderCol =
      period === 'semana' ? 'grooves_semana' : period === 'mes' ? 'grooves_mes' : 'grooves_anio';

    const fetchProfilesTop = async cols => {
      let res = await supabase
        .from('profiles')
        .select(cols)
        .not('phone_verified_at', 'is', null)
        .order(orderCol, { ascending: false })
        .limit(safeLimit);
      if (res.error) {
        const res2 = await supabase
          .from('profiles')
          .select('id, username, avatar_url, grooves')
          .not('phone_verified_at', 'is', null)
          .order('grooves', { ascending: false })
          .limit(safeLimit);
        return res2;
      }
      return res;
    };

    const { data: rpcRows, error: rpcErr } = await supabase.rpc(rpcName, { p_limit: safeLimit });

    if (!rpcErr && Array.isArray(rpcRows) && rpcRows.length > 0) {
      const merged = rpcRows.map(r => mapRpcRow(r, period));
      const seen = new Set(merged.map(r => r.id));

      if (merged.length < safeLimit) {
        const fillRes = await fetchProfilesTop(BASE_PROFILE_SELECT);
        if (!fillRes.error && Array.isArray(fillRes.data)) {
          for (const p of fillRes.data) {
            if (merged.length >= safeLimit) break;
            if (seen.has(p.id)) continue;
            merged.push(mapProfileRow(p));
            seen.add(p.id);
          }
          if (merged.length > rpcRows.length) setPaddedFromProfiles(true);
        }
      }

      setUsedFallback(false);
      const enriched = await enrichRowsWithProfileAvatars(merged.slice(0, safeLimit));
      setRows(enriched);
      setLoading(false);
      return;
    }

    const res = await fetchProfilesTop(BASE_PROFILE_SELECT);

    if (res.error) {
      setError(res.error.message || 'No se pudo cargar el ranking');
      setRows([]);
      setUsedFallback(true);
    } else {
      setUsedFallback(true);
      const enriched = await enrichRowsWithProfileAvatars((res.data || []).map(mapProfileRow));
      setRows(enriched);
    }
    setLoading(false);
  }, [period, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const goProfile = row => {
    const un = row.username || 'usuario';
    onNavigate?.('perfil-vendedor', {
      userId: row.id,
      username: un,
      avatarUrl: row.avatar_url ?? null,
      returnTo: showViewFullLink ? 'comunidad' : 'grooves-ranking',
    });
  };

  const listWrapStyle =
    listMaxHeight != null
      ? { maxHeight: listMaxHeight, overflowY: 'auto', overflowX: 'hidden', marginRight: -4, paddingRight: 4 }
      : undefined;

  return (
    <div
      style={{
        background: BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 12,
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes tg-skel {
          0%, 100% { opacity: 0.38; }
          50% { opacity: 0.92; }
        }
      `}</style>

      <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700, color: C.gold, marginBottom: 10, letterSpacing: '0.02em' }}>
        🎵 Top Grooves
      </div>

      <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', marginBottom: 10, border: `1px solid ${BORDER}` }}>
        {[
          ['semana', 'Semana'],
          ['mes', 'Mes'],
          ['anio', 'Año'],
        ].map(([key, label]) => {
          const on = period === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setPeriod(key)}
              style={{
                flex: 1,
                padding: '7px 4px',
                fontSize: 10,
                fontWeight: on ? 700 : 500,
                border: 'none',
                cursor: 'pointer',
                background: on ? C.gold : 'rgba(30,45,61,0.6)',
                color: on ? '#0a0a0f' : 'rgba(255,255,255,0.55)',
                transition: 'background .15s, color .15s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {usedFallback && !loading && !error && (
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 8, lineHeight: 1.35 }}>
          Orden por saldo Grooves: aplica migración SQL para períodos (semana / mes / año).
        </div>
      )}

      {paddedFromProfiles && !loading && !error && (
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 8, lineHeight: 1.35 }}>
          Incluye también perfiles con 0 Grooves en este período hasta completar el top {limit}.
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: Math.min(limit, 10) }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 36,
                borderRadius: 8,
                background: SKELETON,
                animation: 'tg-skel 1.15s ease-in-out infinite',
                animationDelay: `${i * 0.06}s`,
              }}
            />
          ))}
        </div>
      )}

      {!loading && error && (
        <div style={{ fontSize: 12, color: C.danger, padding: '8px 4px', lineHeight: 1.45 }}>{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', padding: '12px 4px', textAlign: 'center', lineHeight: 1.5 }}>
          Aún no hay datos para este período
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div style={listWrapStyle}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((row, i) => {
              const lvl = levelFromGroovesTotal(profileGroovesEarnedTotal(row));
              const pts = pointsForPeriod(row, period);
              const borderStyle =
                lvl.border === 'transparent' ? '1px solid rgba(255,255,255,0.08)' : `2px solid ${lvl.border}`;
              const boxShadow = lvl.glow ? '0 0 12px rgba(200,168,75,0.45)' : 'none';
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => goProfile(row)}
                    style={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: '28px 36px 1fr auto',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 6px',
                      marginBottom: 2,
                      borderRadius: 8,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: '#fff',
                      transition: 'background .15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(200,168,75,0.06)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: 12, textAlign: 'center', flexShrink: 0 }}>{rankMedal(i)}</span>
                  <span style={{ flexShrink: 0, border: borderStyle, borderRadius: '50%', boxShadow }}>
                    <Avatar
                      initials={profileInitials(row.username)}
                      bg="linear-gradient(145deg, #2a2f3d, #1a1f28)"
                      color="rgba(255,255,255,0.88)"
                      size={34}
                      imageUrl={row.avatar_url}
                    />
                  </span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.username || 'Usuario'}
                      </span>
                      <span style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.38)', marginTop: 2 }}>{lvl.label}</span>
                    </span>
                    <span style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.gold }}>{pts}</span>
                      <span style={{ display: 'block', fontSize: 8, color: 'rgba(255,255,255,0.32)', marginTop: 1 }}>grvs</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showViewFullLink ? (
        <button
          type="button"
          onClick={() => (onViewFullRanking ? onViewFullRanking() : onNavigate?.('grooves-ranking'))}
          style={{
            marginTop: 10,
            width: '100%',
            background: 'none',
            border: 'none',
            color: C.gold,
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            textAlign: 'center',
            padding: '6px 4px',
          }}
        >
          Ver ranking completo →
        </button>
      ) : null}
    </div>
  );
}
